import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInputList } from "@/components/ui/phone-input-list";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ErrorAlert } from "@/lib/components/error-alert";
import type { CustomerEditFormData } from "../../hooks/use-customer-edit";

interface CustomerEditFormProps {
  form: UseFormReturn<CustomerEditFormData>;
  isLoading: boolean;
  error?: Error | null;
}

export function CustomerEditForm({ form, isLoading, error }: CustomerEditFormProps) {
  const { t } = useTranslation("customers");

  return (
    <Form {...form}>
      <div className="space-y-4">
        {error && <ErrorAlert error={error} />}

        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.fields.firstName")}</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isLoading} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.fields.lastName")}</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isLoading} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.fields.emailAddress")}</FormLabel>
              <FormControl>
                <Input {...field} type="email" disabled={isLoading} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phones"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PhoneInputList phones={field.value || []} onChange={field.onChange} errors={{}} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="dateOfBirth"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.fields.dateOfBirth")}</FormLabel>
              <FormControl>
                <Input {...field} type="date" disabled={isLoading} value={field.value || ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.fields.notes")}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={t("form.placeholders.addAdditionalNotes")}
                  className="min-h-[100px]"
                  disabled={isLoading}
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Marketing Consent */}
        <FormField
          control={form.control}
          name="marketingConsent"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-md border">
                <FormControl>
                  <Switch
                    id="marketingConsent"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isLoading}
                  />
                </FormControl>
                <div className="space-y-1">
                  <FormLabel htmlFor="marketingConsent" className="text-sm font-medium">
                    {t("form.fields.marketingCommunications")}
                  </FormLabel>
                  <p className="text-xs text-muted-foreground">
                    {t("form.marketing.consentDescription")}
                  </p>
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </Form>
  );
}
